/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Messages", description = "Create and edit lifecycle messages — iterate with the agent chat or visual editor, manage versions, collaborate through comments and approvals, and export to your ESP.", lastUpdatedDate = new Date(1792627200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Messages",
        description: "Create and edit lifecycle messages — iterate with the agent chat or visual editor, manage versions, collaborate through comments and approvals, and export to your ESP.",
        lastUpdatedDate: "Thu Oct 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Create a message",
          "depth": 0,
          "id": "create-a-message"
        }, {
          "value": "Refine the message",
          "depth": 0,
          "id": "refine-the-message"
        }, {
          "value": "Iterate with the agent chat",
          "depth": 1,
          "id": "iterate-with-the-agent-chat"
        }, {
          "value": "Edit visually",
          "depth": 1,
          "id": "edit-visually"
        }, {
          "value": "Review and approve",
          "depth": 0,
          "id": "review-and-approve"
        }, {
          "value": "Export a message",
          "depth": 0,
          "id": "export-a-message"
        }, {
          "value": "Manage versions",
          "depth": 0,
          "id": "manage-versions"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ol: "ol",
      li: "li",
      h3: "h3",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, creative teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.a, {
                    href: "/lifecycle-studio/overview#get-started",
                    children: "Lifecycle Studio setup complete"
                  })
                }), _jsxs("li", {
                  children: ["At least one ", _jsx(_components.a, {
                    href: "/lifecycle-studio/templates",
                    children: "template"
                  }), " available in your workspace"]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Messages are individual lifecycle emails you create in Lifecycle Studio. Start from a template, refine the email with the agent chat or visual editor, review and approve it, then export the finished email."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["You can create a message two ways: directly in Messages for a quick single email, or as a candidate inside a ", _jsx(_components.a, {
          href: "/lifecycle-studio/campaigns#create",
          children: "campaign"
        }), ". Messages you create directly are listed here, so you can manage and review them in one place."]
      }), "\n", _jsx(_components.p, {
        children: "However you start, the path is the same: generate a draft, refine it, review and approve it, then export it."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-a-message",
        children: "Create a message"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/messages/ls-messages-create-message.png",
          alt: "Email builder with a reference email selected and the agent chat prompt"
        })
      }), "\n", _jsx(_components.p, {
        children: "To create a new message:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "https://app.hightouch.com/lifecycle-studio/messages?from=docs",
              children: "Lifecycle Studio → Messages"
            })
          }), " and select ", _jsx(_components.strong, {
            children: "New message"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a ", _jsx(_components.strong, {
            children: "reference email"
          }), " for the agent to follow as a structure and style guide. Choose one of your ", _jsx(_components.a, {
            href: "/lifecycle-studio/templates",
            children: "templates"
          }), ", or select ", _jsx(_components.strong, {
            children: "Import new"
          }), " to paste raw HTML. A reference email is required."]
        }), "\n", _jsx(_components.li, {
          children: "Describe what you want. The agent generates the email from your description, the reference email, and your workspace context."
        }), "\n", _jsx(_components.li, {
          children: "Review the generated message and iterate."
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Include your target audience, key message, and desired tone in the description. Specific inputs produce more relevant content on the first pass."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "refine-the-message",
        children: "Refine the message"
      }), "\n", _jsx(_components.p, {
        children: "The agent's first draft is a starting point. Refine it until it's ready: chat with the agent, edit the email directly, or move between the two. Edits in one are reflected in the other."
      }), "\n", _jsx(_components.h3, {
        id: "iterate-with-the-agent-chat",
        children: "Iterate with the agent chat"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/messages/ls-messages-agent.png",
          alt: "Email editor with agent chat panel and rendered email preview"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once the agent generates a message, you land in the email editor, with the agent chat and a live preview side by side. Refine the message by chatting with the agent in natural language. Ask for changes to copy, imagery, layout, tone, subject line, and preheader."
      }), "\n", _jsx(_components.p, {
        children: "Example prompts:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "\"Make the CTA more urgent.\""
        }), "\n", _jsx(_components.li, {
          children: "\"Swap the hero image for a lifestyle shot.\""
        }), "\n", _jsx(_components.li, {
          children: "\"Shorten the body copy.\""
        }), "\n", _jsx(_components.li, {
          children: "\"Try a more conversational tone in the intro paragraph.\""
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The agent explains what it changed and why, so you can evaluate each iteration in context."
      }), "\n", _jsx(_components.h3, {
        id: "edit-visually",
        children: "Edit visually"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/messages/ls-messages-editor-selected-element.png",
          alt: "Selected element with typography and link editing controls"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can also make changes by hand:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Direct editing"
          }), " — Select any element in the preview to change its text, typography, color, and links."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "HTML editing"
          }), " — Switch to the code view to edit the underlying HTML."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Preview modes"
          }), " — Preview the email in web, mobile, light, and dark modes to check how it renders across contexts."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To show different recipients different versions of a piece of the email, such as a hero image for each loyalty tier, make that piece ", _jsx(_components.a, {
          href: "/lifecycle-studio/dynamic-content",
          children: "dynamic"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "review-and-approve",
        children: "Review and approve"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/messages/ls-messages-review-approve.png",
          alt: "Agent feedback for a message in the editor"
        })
      }), "\n", _jsx(_components.p, {
        children: "Before you export, you can run two kinds of review."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Agent feedback"
        }), " — Have your agent reviewers check the message before you involve teammates. Each reviewer checks it against its instructions and reference files, such as legal guidelines, brand guidelines, or email QA checklists, and flags issues as suggestions, warnings, or critical. Agent reviewers are assigned in ", _jsx(_components.a, {
          href: "/agents/settings",
          children: "Agents settings"
        }), " and configured by creating ", _jsx(_components.a, {
          href: "/agents/custom-agents",
          children: "custom agents"
        }), ". If you don't have any reviewers assigned, you can still use Approval for human sign-off."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Approval"
        }), " — When you're ready for human sign-off, invite teammates as approvers and track their approval status before you export."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/messages/ls-messages-approval.png",
          alt: "Approval review with the Invite approvers button"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "export-a-message",
        children: "Export a message"
      }), "\n", _jsxs(_components.p, {
        children: ["When a message is ready, export it with the ", _jsx(_components.strong, {
          children: "Export"
        }), " dropdown:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Export to email provider"
              })
            }), _jsx(_components.td, {
              children: "Create a new HTML template in your connected ESP, or update an existing one."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Save to template"
              })
            }), _jsxs(_components.td, {
              children: ["Save the message back to the ", _jsx(_components.a, {
                href: "/lifecycle-studio/templates",
                children: "template library"
              }), " for future reuse."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Download HTML"
              })
            }), _jsx(_components.td, {
              children: "Download the raw HTML file."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Download PDF"
              })
            }), _jsx(_components.td, {
              children: "Download a PDF rendering of the message."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Download PNG"
              })
            }), _jsx(_components.td, {
              children: "Download the message as a PNG image."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["The Export menu also includes image-specific options under ", _jsx(_components.strong, {
          children: "Images"
        }), ": ", _jsx(_components.strong, {
          children: "Download"
        }), " the images used in the message, ", _jsx(_components.strong, {
          children: "Save to library"
        }), ", or ", _jsx(_components.strong, {
          children: "Export images to provider"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Exporting to your ESP creates or updates a template there. The actual send happens in your ESP, where you manage timing, frequency capping, and delivery rules."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "manage-versions",
        children: "Manage versions"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/messages/ls-messages-version-dropdown.png",
          alt: "Version history dropdown"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Each message keeps a version history as you work. Open ", _jsx(_components.strong, {
          children: "Version history"
        }), " to browse saved versions with their author and timestamp, then select any previous version to view or restore it."]
      }), "\n", _jsx(_components.p, {
        children: "Versions are created when the agent generates new content or when you save changes. This lets you compare iterations and roll back if a previous version was stronger."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/campaigns",
            children: "Campaigns"
          }), " — Plan and send end-to-end lifecycle campaigns"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/templates",
            children: "Templates"
          }), " — Manage the structural starting points for messages"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/images",
            children: "Images"
          }), " — Manage the image library the agent draws from"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/dynamic-content",
            children: "Dynamic content"
          }), " — Show each recipient their own version of an image, block, or button"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
